import { useTranslation } from 'react-i18next';

import { DefaultVendorCostState } from 'contacts/enums';
import { SubtitleCard } from 'core/components/SubtitleCards/SubtitleCard';
import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { TextEllipsis } from 'core/elements';
import { PricingHelper } from 'pricing/helpers';
import { Color, Level } from 'theme/enums';

interface IProps {
  groupName: string;
  vendorName?: string;
  standardCost?: number | null;
  purchaseCost?: number | null;
  uom?: string;
  costState?: DefaultVendorCostState;
  highlighted?: boolean;
}

const baseCardSx = { width: 240, flex: 'none' };
// INVENTORY_ONLY only comes from the inventory call; treated like UNAVAILABLE if it lands here.
const COST_HIDDEN_STATES = new Set<DefaultVendorCostState>([
  DefaultVendorCostState.CONTACT_SALES_REP,
  DefaultVendorCostState.UNAVAILABLE,
  DefaultVendorCostState.INVENTORY_ONLY,
]);

const messageSx = { fontWeight: 600, textAlign: 'left' } as const;

const highlightedSx = {
  ...baseCardSx,
  backgroundColor: 'primary.softBg',
  border: '1px solid',
  borderColor: 'primary.softBg',
};

export const VendorCostCard = ({
  groupName,
  vendorName,
  standardCost,
  purchaseCost,
  uom,
  costState,
  highlighted,
}: IProps) => {
  const { t } = useTranslation();

  const isContactSalesRep = costState === DefaultVendorCostState.CONTACT_SALES_REP;
  const isCostHidden = !!costState && COST_HIDDEN_STATES.has(costState);

  const standardCostDisplay =
    !isCostHidden && standardCost != null
      ? PricingHelper.formatPricingPerUom(standardCost, uom)
      : DEFAULT_PLACEHOLDER;

  const purchaseCostDisplay =
    !isCostHidden && standardCost != null && purchaseCost != null
      ? PricingHelper.formatPricingPerUom(purchaseCost, uom)
      : '';

  return (
    <SubtitleCard
      label={groupName}
      subtitle={vendorName}
      value={standardCostDisplay}
      extra={purchaseCostDisplay}
      options={{
        cardSx: highlighted ? highlightedSx : baseCardSx,
        contentSx: { justifyContent: 'space-between' },
        labelProps: { level: Level.TITLE_MD, color: null },
        valueElement: isContactSalesRep ? (
          <TextEllipsis
            content={t('messageContactSalesRepForAccess')}
            level={Level.TITLE_SM}
            color={Color.WARNING}
            maxRows={2}
            sx={messageSx}
          />
        ) : undefined,
      }}
    />
  );
};
